// Live updates over /ws while signed in: presence (admins), ticket events, announcements, kicks.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "./auth.tsx";
import { useToast } from "./ui/index.tsx";
import type { OnlineClient, ServerMessage } from "../shared/types.ts";

type Listener = (msg: ServerMessage) => void;

interface LiveState {
  connected: boolean;
  /** Admins only; empty for everyone else. */
  online: OnlineClient[];
  subscribe: (fn: Listener) => () => void;
}

const LiveContext = createContext<LiveState>({ connected: false, online: [], subscribe: () => () => {} });

const HEARTBEAT_MS = 30_000;

export function LiveProvider({ children }: { children: ReactNode }) {
  const { me, logout } = useAuth();
  const toast = useToast();
  const [connected, setConnected] = useState(false);
  const [online, setOnline] = useState<OnlineClient[]>([]);
  const listeners = useRef(new Set<Listener>());

  useEffect(() => {
    if (!me) return;
    let ws: WebSocket | null = null;
    let heartbeat: ReturnType<typeof setInterval> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let stopped = false;

    const connect = () => {
      ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`);
      ws.onopen = () => {
        attempts = 0;
        setConnected(true);
        heartbeat = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: "heartbeat" })), HEARTBEAT_MS);
      };
      ws.onmessage = (ev) => {
        let msg: ServerMessage;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === "presence") setOnline(msg.online);
        if (msg.type === "announcement") toast(msg.title, msg.level === "critical" ? "error" : "info");
        if (msg.type === "kill") { stopped = true; toast("You were signed out", "error"); void logout(); }
        for (const fn of listeners.current) fn(msg);
      };
      ws.onclose = () => {
        setConnected(false);
        clearInterval(heartbeat);
        if (!stopped) retry = setTimeout(connect, Math.min(30_000, 1000 * 2 ** attempts++));
      };
    };
    connect();

    return () => {
      stopped = true;
      clearInterval(heartbeat);
      clearTimeout(retry);
      ws?.close();
      setConnected(false);
      setOnline([]);
    };
  }, [me?.id]);

  const subscribe = useCallback((fn: Listener) => { listeners.current.add(fn); return () => { listeners.current.delete(fn); }; }, []);
  const value = useMemo<LiveState>(() => ({ connected, online, subscribe }), [connected, online, subscribe]);
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export const useLive = () => useContext(LiveContext);

/** Runs `fn` for every live message of the given types. */
export function useLiveEvent(types: ServerMessage["type"][], fn: Listener) {
  const { subscribe } = useLive();
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => subscribe((m) => { if (types.includes(m.type)) ref.current(m); }), [subscribe, types.join()]);
}
